import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Redirect, Link } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import PageTitle from "../../layouts/PageTitle";
import Toast, {
  notifyError,
  notifyTopRight,
  notifyWarning,
} from "../toast/SetToast";
import {
  ANNUAL_MASK_ACCESS_COLLECTION,
  normalizeAnnualMaskAccessEmail,
} from "../Followup/annualMaskPurchaseUtils";

const isEmail = (value) => value && value.includes("@");

const AnnualMaskAccessConfig = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState("");
  const roles =
    currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];
  const isSuperAdmin = roles.includes("super_admin");

  const loadAccessEmails = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(
        collection(db, ANNUAL_MASK_ACCESS_COLLECTION),
      );
      const nextEmails = snapshot.docs
        .map((accessDoc) =>
          normalizeAnnualMaskAccessEmail(
            (accessDoc.data() || {}).email || accessDoc.id,
          ),
        )
        .filter(isEmail);

      setEmails(Array.from(new Set(nextEmails)).sort());
    } catch (error) {
      console.error("Unable to load Annual Mask Access", error);
      notifyError(
        `Unable to load Annual Mask Access: ${(error && error.message) || error}`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) loadAccessEmails();
  }, [isSuperAdmin, loadAccessEmails]);

  if (!isSuperAdmin) return <Redirect to="/" />;

  const addEmail = async (event) => {
    event.preventDefault();
    const email = normalizeAnnualMaskAccessEmail(emailInput);

    if (!isEmail(email)) {
      notifyWarning("Enter a valid account email.");
      return;
    }
    if (emails.includes(email)) {
      notifyWarning("This account already has Annual Mask Access.");
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, ANNUAL_MASK_ACCESS_COLLECTION, email), {
        email,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.displayName || currentUser.email || "",
      });
      setEmails((current) => [...current, email].sort());
      setEmailInput("");
      notifyTopRight("Annual Mask Access added.");
    } catch (error) {
      console.error("Unable to add Annual Mask Access", error);
      notifyError(
        `Unable to add Annual Mask Access: ${(error && error.message) || error}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const removeEmail = async () => {
    if (!pendingRemoval) return;

    setSaving(true);
    try {
      await deleteDoc(
        doc(db, ANNUAL_MASK_ACCESS_COLLECTION, pendingRemoval),
      );
      setEmails((current) =>
        current.filter((email) => email !== pendingRemoval),
      );
      setPendingRemoval("");
      notifyTopRight("Annual Mask Access removed.");
    } catch (error) {
      console.error("Unable to remove Annual Mask Access", error);
      notifyError(
        `Unable to remove Annual Mask Access: ${(error && error.message) || error}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Toast />
      <PageTitle activeMenu="Annual Mask Access" motherMenu="Admin" />

      <Modal
        centered
        show={Boolean(pendingRemoval)}
        onHide={() => {
          if (!saving) setPendingRemoval("");
        }}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Remove Annual Mask Access?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove top-right Annual Mask Purchase notifications and order access
          for <strong>{pendingRemoval}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setPendingRemoval("")}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={removeEmail} disabled={saving}>
            {saving ? "Removing…" : "Remove access"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">
            Annual Mask notification and order access ({emails.length})
          </h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            Accounts listed here receive the top-right notification when an
            annual mask case becomes ready to purchase. Access is controlled
            by this email list; a role, including Super Admin, does not receive
            the notification automatically.
          </p>
          <p className="text-muted mb-3">
            Configure outgoing sender addresses for Annual Mask and Booking Calendar in <Link to="/email-sender-settings">Email Sender Settings</Link>.
          </p>

          <form
            className="d-flex flex-wrap align-items-end mb-3"
            onSubmit={addEmail}
          >
            <div className="mr-2 mb-2" style={{ minWidth: 280 }}>
              <label className="mb-1" htmlFor="annual-mask-access-email">
                Account email
              </label>
              <input
                id="annual-mask-access-email"
                type="email"
                className="form-control"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                placeholder="name@example.com"
                disabled={saving || loading}
              />
            </div>
            <Button type="submit" className="mb-2" disabled={saving || loading}>
              {saving ? "Saving…" : "Add access"}
            </Button>
          </form>

          {loading ? (
            <div className="text-muted">Loading configured emails…</div>
          ) : emails.length ? (
            <div className="d-flex flex-wrap">
              {emails.map((email) => (
                <span className="badge badge-primary mr-2 mb-2 p-2" key={email}>
                  {email}
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-white p-0 ml-2"
                    onClick={() => setPendingRemoval(email)}
                    disabled={saving}
                    aria-label={`Remove ${email}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-muted">
              No Annual Mask Access emails are configured.
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default AnnualMaskAccessConfig;
